/*    
<summary>
  This functional component "Add Headquarter Management Form" provides form to add new headquarter or update existing headquarter.
  Developer:Mohammad Saquib Khan, Created Date:09-Apr-2025
</summary>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import emailTypeEnum from "../../../constants/enums/email-type-enum";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { IObservableInitialState } from "../../../models/ICommon";
import { IHQStoreEmailSettings } from "../../../models/response/IEmailManagementResponse";
import LoaderButton from "../../../shared-components/button/loader-button";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikReactQuill from "../../../shared-components/FormikFormHandlers/FormikFormReactQuill";
import { EmailSettingValidateSchema } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";

interface IProps {
  emailType: emailTypeEnum;
  userType: string;
  isLoading: boolean;
  disabled: boolean;
  disableAction: boolean;
  submitHandler: (data: any) => void;
  initialValues: IHQStoreEmailSettings;
  addUpdateEmailSettingState: IObservableInitialState;
}

const AddUpdateEmailSettingForm = (props: IProps) => {
  const { emailType, isLoading, submitHandler, userType, disabled, addUpdateEmailSettingState, disableAction } = props;
  const [resetFormValues, setResetFormValues] = useState<(() => void) | null>(null);
  
  const hasReset = useRef(false);

  useEffect(() => {
    if (addUpdateEmailSettingState.error && resetFormValues && !hasReset.current) {
      resetFormValues();
      hasReset.current = true; // prevent further resets
    } else if (!addUpdateEmailSettingState.success) {
      hasReset.current = false; // allow reset again on next success
    }
  }, [addUpdateEmailSettingState.error, resetFormValues]);
  
  const getTitle = () => {
    switch (emailType) {
      case emailTypeEnum.VisitingThanks:
        return "thank_you_email";
      case emailTypeEnum.ReservationReminder:
        return "reservation_reminder_email";
      case emailTypeEnum.HappyBirthday:
        return "birthday_email";
      default:
        return "email";
    }
  };

  return (
    <>
      <Formik
        initialValues={{...props.initialValues, EmailType: emailType}}
        enableReinitialize
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={EmailSettingValidateSchema(userType)}
        onSubmit={async (values, { resetForm }) => {
          setResetFormValues(() => resetForm);
          submitHandler(values);
        }}
      >
        {({ setFieldValue, values }) => (
          <Form className="user-form p-3">
            {isLoading && <FormLoader loading={isLoading} />}
              <div className="form-group mb-3 row">
                <h3>
                  <FormattedMessage id={getTitle()} /> :
                </h3>
                {emailType === emailTypeEnum.VisitingThanks && (
                  <>
                    <div className="d-flex align-items-start mb-2">
                      <span className="me-2 mt-2">
                        <FormattedMessage id="accounting_email" />
                      </span>
                      <div>
                        <Field
                          className="form-control mw-50"
                          name="EmailAfterHours"
                          type="number"
                          placeholder="enter_hours"
                          as={FormikFormInput}
                          disabled={disabled}
                        />
                      </div>
                      <span className="ms-2 mt-2">
                        <FormattedMessage id="sent_afer_hours" />
                      </span>
                    </div>
                    <div className="form-group">
                      <span className="me-2">
                        <FormattedMessage id="email_body" />
                      </span>
                      <div className="px-0">
                        <Field
                          type="text"
                          name="EmailBody"
                          as={FormikReactQuill}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </>
                )}
                {emailType === emailTypeEnum.ReservationReminder && (
                  <>
                    <div className="d-flex align-items-center mb-2">
                      <span className="me-2">
                        <FormattedMessage id="reservation_email" />
                      </span>
                      <div>
                        <Field
                          className="form-control mw-50"
                          name="EmailBeforeHours"
                          type="number"
                          placeholder="enter_hours"
                          as={FormikFormInput}
                          disabled={disabled}
                        />
                      </div>
                      <span className="ms-2">
                        <FormattedMessage id="sent_before_hours" />
                      </span>
                    </div>
                    <div className="form-group">
                      <span className="me-2">
                        <FormattedMessage id="email_body" />
                      </span>
                      <div className="px-0">
                        <Field
                          type="text"
                          name="EmailBody"
                          as={FormikReactQuill}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </>
                )}
                {emailType === emailTypeEnum.HappyBirthday && (
                  <>
                    <div className="d-flex align-items-start form-group">
                      <span className="me-2 mt-2">
                        <FormattedMessage id="birthday_email_label" />
                      </span>
                      <div>
                        <Field
                          className="form-control mw-50"
                          name="BeforeDays"
                          type="number"
                          placeholder="enter_days"
                          as={FormikFormInput}
                          disabled={disabled}
                        />
                      </div>
                      <span className="ms-2 mt-2 me-4">
                        <FormattedMessage id="days_before" />
                      </span>
                      <div>
                        <Field
                          className="form-control mw-50"
                          name="BeforeHours"
                          type="number"
                          placeholder="enter_hours"
                          as={FormikFormInput}
                          disabled={disabled}
                        />
                      </div>
                      <span className="ms-2 mt-2">
                        <FormattedMessage id="o_clock" />
                      </span>
                    </div>

                    <div className="form-group">
                      <span className="me-2">
                        <FormattedMessage id="email_header" />
                      </span>
                      <Field
                          type="text"
                          name="EmailHeader"
                          as={FormikReactQuill}
                          disabled={disabled}
                        />
                    </div>
                    {userType === userTypeEnum[userTypeEnum.Store] && (
                      <div className="form-group">
                        <span className="me-2">
                          <FormattedMessage id="email_body" />
                        </span>
                        <div className="px-0">
                          <Field
                            type="text"
                            name="StoreEmailBody"
                            as={FormikReactQuill}
                          />
                        </div>
                      </div>
                    )}
                    <div className="form-group">
                      <span className="me-2">
                        <FormattedMessage id="email_footer" />
                      </span>
                      <div className="px-0">
                        <Field
                          type="text"
                          name="EmailFooter"
                          as={FormikReactQuill}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </>
                )}
                {userType === userTypeEnum[userTypeEnum.Store] && (
                  <div className="form-group">
                    <div className="form-check form-switch d-flex align-items-center p-0">
                      <label htmlFor="IsSend" className="form-check-label">
                        <FormattedMessage id="is_send" /> :
                      </label>{" "}
                      <Field
                        className="form-check-input ms-2"
                        name="IsSend"
                        type="checkbox"
                        id="IsSend"
                      />
                    </div>
                  </div>
                )}
              </div>
              <LoaderButton
                type="submit"
                iconName="bi-check-lg"
                isLoading={props.isLoading}
                disabled={disableAction}
                text="add"
              />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddUpdateEmailSettingForm;
